import {
  deleteV1CompaniesCompanyIdCatenaLinkedFleetsId,
  getV1CompaniesCompanyIdCatenaLinkedFleets,
  postV1CompaniesCompanyIdCatenaLinkedFleets,
  Telematics_Catena_LinkedFleet_Create,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import connection from '~services/connectionModule';
import { extractPagination, PaginationLink } from '~services/pagination';
import { useStores } from '~store';

import { CatenaLinkedFleet } from './models';

export default function useCatena() {
  const { userStore } = useStores();

  const createLinkedFleet = useCallback(
    async (body: Telematics_Catena_LinkedFleet_Create) => {
      try {
        const response = await postV1CompaniesCompanyIdCatenaLinkedFleets({
          body,
          path: { 'company-id': userStore.userCompany?.id ?? '' },
        });

        return CatenaLinkedFleet.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.catena.failed_to_create_linked_fleet'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [userStore],
  );

  const disconnectLinkedFleet = useCallback(
    async (id: string) => {
      try {
        const response = await deleteV1CompaniesCompanyIdCatenaLinkedFleetsId({
          path: { 'company-id': userStore.userCompany?.id ?? '', id },
        });

        return CatenaLinkedFleet.parse(response.data.data);
      } catch (error) {
        try {
          connection.handleRequestError(
            error,
            t('error_messages.catena.failed_to_disconnect_linked_fleet'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [userStore],
  );

  const listLinkedFleets = useCallback(
    async (options?: {
      cursor?: string;
      link?: PaginationLink;
      signal?: AbortSignal;
    }) => {
      const { cursor, link, signal } = options ?? {};
      try {
        const response = await getV1CompaniesCompanyIdCatenaLinkedFleets({
          path: { 'company-id': userStore.userCompany?.id ?? '' },
          query: link && cursor ? { [`page[${link}]`]: cursor } : {},
          signal,
        });

        return {
          data: response.data.data.map(CatenaLinkedFleet.parse),
          pagination: extractPagination(response),
        };
      } catch (error) {
        // This catch resolves to null, so a cancelled request must rethrow to stay cancelled.
        if (signal?.aborted) {
          throw error;
        }

        try {
          connection.handleRequestError(
            error,
            t('error_messages.catena.failed_to_fetch_linked_fleets'),
          );
        } catch {
          // Error handling side effects already executed in connection.handleRequestError.
        }

        return null;
      }
    },
    [userStore],
  );

  return { createLinkedFleet, disconnectLinkedFleet, listLinkedFleets };
}
